import { VoronoiMazeData, VoronoiMazeState, VoronoiRegion } from '../types/maze-types';
import { fitGeometryToBox } from './voronoiFit';

/**
 * Raw JSON from the worker/generator script.
 * Coordinates are normalized (0..1). Regions match VoronoiMazeData format.
 */
interface TemplateConfig {
  regions: Array<{
    id: number;
    vertices: Array<{ x: number; y: number }>;
    site: { x: number; y: number };
    neighbors: number[];
    allNeighbors?: number[];
  }>;
  entryRegionId: number;
  exitRegionId: number;
  contour?: Array<{ x: number; y: number }>;
}

/**
 * Convert a pre-computed template config into VoronoiMazeData FITTED to a box:
 * the silhouette's bounding box (not the worker's square) fills `maxW × maxH`
 * with uniform scale, so a wide dragon is as big as a tall cat. Returns the data
 * and the canvas size it now needs.
 */
export function convertTemplateConfig(
  config: TemplateConfig,
  maxW: number,
  maxH: number,
): { mazeData: VoronoiMazeData; canvasSize: { width: number; height: number } } {
  const regions: VoronoiRegion[] = config.regions.map((r) => ({
    id: r.id,
    vertices: r.vertices.map((v) => ({ ...v })),
    site: { ...r.site },
    neighbors: r.neighbors,
    allNeighbors: r.allNeighbors,
  }));
  const mazeData: VoronoiMazeData = {
    regions,
    entryRegionId: config.entryRegionId,
    exitRegionId: config.exitRegionId,
    contour: config.contour?.map((v) => ({ ...v })),
  };
  const canvasSize = fitGeometryToBox(mazeData, maxW, maxH);
  return { mazeData, canvasSize };
}

/**
 * Load a pre-computed template JSON and return a VoronoiMazeState
 * ready for the renderer.
 *
 * @param configPath Public URL path (e.g. "/maze-templates/cat-medium.json")
 * @param maxW       Available play-area width in pixels (the silhouette is fitted into it)
 * @param maxH       Available play-area height in pixels
 */
export async function loadTemplateConfig(
  configPath: string,
  maxW?: number,
  maxH?: number,
): Promise<VoronoiMazeState> {
  const response = await fetch(configPath);
  if (!response.ok) {
    throw new Error(`Failed to load template config: ${response.status} ${response.statusText}`);
  }

  const config: TemplateConfig = await response.json();
  const { mazeData, canvasSize } = convertTemplateConfig(config, maxW ?? 800, maxH ?? 800);

  return {
    mazeData,
    canvasSize,
    pathSegments: [],
    isCompleted: false,
  };
}
